import { useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { useParams } from 'react-router-dom';

import {
  PATH_INTERNAL_THIRD_PARTIES_DEFAULT,
  PATH_EXTERNAL_THIRD_PARTIES_DEFAULT,
  PATH_INTERNAL_ORGANIZATIONS,
  PATH_EXTERNAL_ORGANIZATIONS,
  PATH_ORGANIZATION_DEFAULT,
} from '../../app/constants/paths.constant';
import { Breadcrumbs } from '../../components/Breadcrumbs';
import { Loading } from '../../components/Loading';
import { useIdParam } from '../../utils/use-id-param.util';
import { Page } from '../../contexts/NavigationContext/enums/page.enum';
import { useNavigationContext } from '../../contexts/NavigationContext/hooks/useNavigationContext.hook';
import { useBuildPath } from '../../hooks/useBuildPath.hook';
import { buildDocumentTitle } from '../../utils/document-title.util';
import { PageTitle } from '../../components/PageTitle';

import { OrganizationTabs } from './Tabs';
import { useFindOneOrganizationQuery } from './hooks/useFindOneOrganizationQuery.hook';

export const OrganizationPage = () => {
  const { setPage } = useNavigationContext();
  const id = useIdParam();
  const { addressId } = useParams();

  const path = useBuildPath(PATH_ORGANIZATION_DEFAULT, {
    id,
    addressId,
  });

  const { data, isSuccess } = useFindOneOrganizationQuery(id);

  const organization = data?.findOneOrganization;

  useEffect(() => {
    setPage(
      organization?.isInternal
        ? Page.InternalThirdParties
        : Page.ExternalThirdParties,
    );
  }, [organization?.isInternal, setPage]);

  const breadcrumbs = organization?.isInternal
    ? [
        {
          to: PATH_INTERNAL_THIRD_PARTIES_DEFAULT,
          label: 'organisation socna',
          key: 'internalThirdParties',
        },
        {
          to: PATH_INTERNAL_ORGANIZATIONS,
          label: 'sociétés',
          key: 'internalOrganizations',
        },
        {
          to: path,
          label: 'fiche',
          key: 'internalOrganization',
        },
      ]
    : [
        {
          to: PATH_EXTERNAL_THIRD_PARTIES_DEFAULT,
          label: 'tiers',
          key: 'externalThirdParties',
        },
        {
          to: PATH_EXTERNAL_ORGANIZATIONS,
          label: 'organisations',
          key: 'externalOrganizations',
        },
        {
          to: path,
          label: 'fiche',
          key: 'externalOrganization',
        },
      ];

  const beforeTitleContent = (
    <>
      <Helmet>
        <title>
          {buildDocumentTitle(
            `Fiche ${organization?.isInternal ? 'Société' : 'Organisation'}`,
            organization?.code,
          )}
        </title>
      </Helmet>
      <Breadcrumbs paths={breadcrumbs} />
    </>
  );

  const titleContent = (
    <PageTitle
      title={`Fiche ${organization?.isInternal ? 'Société' : 'Organisation'}`}
    />
  );

  if (isSuccess) {
    const organization = data.findOneOrganization;

    return (
      <>
        {beforeTitleContent}
        <PageTitle code={organization.code} title={organization.name} />
        <OrganizationTabs organization={organization} />
      </>
    );
  }

  return (
    <>
      <Loading />
      {beforeTitleContent}
      {titleContent}
    </>
  );
};
